/**
 * shop-overlay.ts — 局內金幣商店 DOM (Phase 2, §4-1)。
 *
 * 復用 upgrade-picker 的 `.upgrade-card` 版型 (同一份 CSS),關卡間 / 魔王前彈出。
 * 商品 = registry 內標 `price` 的 Upgrade;買 = 扣 coins → 套用 (沿用 scene 的
 * applyUpgradeInScene 同一條 apply,效果與三選一一致)。
 *
 * 鐵則(學習與遊戲分離):金幣/商店全局內,打完即丟。本 UI 只讀 getCoins()、
 * 呼 onBuy() 扣幣套用,**絕不碰 saveProgress / 修煉值**。買得起才亮、買不起灰、
 * 不做刷新/議價 (K-3 友善)。
 */

import type { Upgrade } from '../systems/upgrade-pool';
import { resolveRarity } from './upgrade-picker';

export interface ShopOverlayCallbacks {
  /** 取得當前可用金幣 (scene 讀 mods.coins)。每次重繪都會呼叫以更新 gating。 */
  getCoins: () => number;
  /**
   * 玩家點「買」。回傳是否成功扣款 (scene 內呼 mods.spendCoins → applyUpgradeInScene)。
   * 成功 → UI 標記該卡售出 + 重繪 gating;失敗 (買不起) → 不動。
   */
  onBuy: (u: Upgrade) => boolean;
  /** 玩家點「離開」或買完關閉 → scene 推進下一波 / 開打魔王。 */
  onClose: () => void;
}

/**
 * 顯示金幣商店。`offers` = shopOffer() 挑好的 3~4 件商品。
 * 玩家可連續買多件 (買得起就亮),按「離開」結束。
 */
export function showShopOverlay(offers: Upgrade[], cb: ShopOverlayCallbacks): void {
  const root = document.createElement('div');
  // 復用 upgrade-picker 的 class,沿用同一份視覺;外加 shop hook class 供日後微調。
  root.className = 'upgrade-picker shop-overlay';

  const sold = new Set<string>();

  const render = (): void => {
    const coins = cb.getCoins();
    root.innerHTML = `
      <h2>🏪 補給商店</h2>
      <div class="shop-coins">💰 金幣 ${coins}</div>
      <div class="upgrade-grid"></div>
      <button class="shop-close">離開</button>
    `;
    const grid = root.querySelector('.upgrade-grid')!;
    for (const u of offers) {
      const rarity = resolveRarity(u);
      const price = typeof u.price === 'number' ? u.price : 0;
      const isSold = sold.has(u.id);
      const affordable = !isSold && coins >= price;
      const card = document.createElement('button');
      card.className = `upgrade-card rarity-${rarity}` + (affordable ? '' : ' shop-locked');
      card.dataset.rarity = rarity;
      card.dataset.price = String(price);
      card.disabled = !affordable;
      card.innerHTML = `
        <div class="up-emoji">${u.emoji}</div>
        <div class="up-name">${u.displayName}</div>
        <div class="up-desc">${u.description}</div>
        <div class="shop-price">${isSold ? '✅ 已購買' : '💰 ' + price}</div>
      `;
      card.addEventListener('click', () => {
        if (!affordable) return;
        const ok = cb.onBuy(u);
        if (ok) { sold.add(u.id); render(); } // 重繪以更新金幣 + gating。
      });
      grid.appendChild(card);
    }
    const closeBtn = root.querySelector('.shop-close')!;
    closeBtn.addEventListener('click', () => {
      root.remove();
      cb.onClose();
    });
  };

  render();
  document.body.appendChild(root);
}
